<template>
  <div class="e-block-wrap">
    <div class="e-block">
      <div class="e-block__scroll" :style="{ minHeight: containerHeight }">
        <template v-if="!isLoading">
          <template v-if="formattedProducts.length > 0">
            <div class="e-block__tabs-group" v-if="formattedProducts.length > 1">
              <div class="e-block__tabs-wrap">
                <div class="e-block__tabs">
                  <div class="e-block__tab"
                       :class="{'e-block__tab--active': activeTab === index}"
                       @click="setTab(index)"
                       v-for="(product, index) in formattedProducts" :key="`tab-${index}`">
                    <div class="e-block__tab-img" :style="{ backgroundImage: `url(${product.img})` }" />
                    <div class="e-block__tab-name">{{ product.model }}</div>
                  </div>
                </div>
              </div>
              <div class="e-block__tabs-legend">
                <div class="e-row e-row--justify-between">
                  <div class="e-col-auto"><div class="e-voltage e-voltage--low">Lowest {{ isMiniTank ? 'Capacity' : 'kW' }}</div></div>
                  <div class="e-col-auto"><div class="e-voltage e-voltage--direction"></div></div>
                  <div class="e-col-auto"><div class="e-voltage e-voltage--high">Highest {{ isMiniTank ? 'Capacity' : 'kW' }}</div></div>
                </div>
              </div>
            </div>
            <div class="e-block__content" ref="resultContent">
              <div class="e-block__tab-item" :class="{'e-block__tab-item--active': activeTab === index}" v-for="(product, index) in formattedProducts" :key="`tab-item-${index}`">
                <div class="e-row e-row--gutters">
                  <div class="e-col e-col-sm-12 e-order-sm-2">
                    <div class="e-product-detail">
                      <div class="e-product-detail__info">
                        <div class="e-block__label" v-if="!isAutoBuster && !showQuantity">Congrats! You have sized & selected this model below:</div>
                        <div class="e-block__title e-block__title--mb16">{{ product.model }} {{ withCabinet ? `${selectedCabinet.name} ${cabinetOptions}` : '' }}</div>
                        <div class="e-block__quantity e-block__title--mb16" v-if="showQuantity">You picked {{ product.quantity }} of these products</div>
                        <div class="e-block__text" v-if="isAutoBuster">AutoBooster extends the useful capacity of a traditional tank water heater up to 45%*</div>
                        <div class="e-block__badge-block" v-if="isAutoBuster">
                          <info-text text="Electric">Verify the electric tank water heater is powered through a 30-amp circuit breaker in the circuit breaker panel. Do not install an AutoBooster on an electric tank water heater powered through a 40-amp or larger circuit breaker.</info-text>
                          <info-text text="Gas, oil, hybrid">An electrician is required to install a 240V/208V 30-amp circuit for an AutoBooster</info-text>
                        </div>
                        <product-link :link="doc.SEOURL" v-for="(doc, indexDoc) in product.documents" :key="`doc-${indexDoc}`"> {{ doc.DocumentTitle }}</product-link>
                        <product-link v-if="product.nemaCabinets.length > 0 && selectedFlow === FLOW_TYPE.COMMERCIAL && !withCabinet" @click="selectCabinet(product)">Additional options</product-link>
                        <product-link :link="whereToByLink" type="red">Where To Buy</product-link>
                        <product-link :link="product.url">Additional Resources</product-link>
                        <div class="e-block__text e-block__text--buster" v-if="isAutoBuster">
                          <p>It is recommended to install an AutoBooster if your existing hot water tank is electric, gas, oil, or electric hybrid (heat pump). If you get hot water from your boiler through a tankless coil or an indirect tank, then it is not recommended.</p>
                          <p><span class="e-red">*</span> This value is extremely dependent on how fast you are running your flow through this unit. The faster the flow rate, the lower the extension percentage will be.</p>
                        </div>
                        <div class="e-block__text e-block__text--buster" v-if="product.OriginalKilowatts && product.OriginalKilowatts !== product.Kilowatts">This heating unit can be connected to {{ product.Voltage }}V with a 25% reduced kW rating</div>
                      </div>
                    </div>
                  </div>
                  <div class="e-col e-col-sm-12 e-order-sm-1 e-col--align-end">
                    <img :src="withCabinet ? selectedCabinet.img : product.img" :alt="product.model" class="e-product-detail__img"/>
                  </div>
                </div>
              </div>
            </div>
          </template>
          <need-help-block :no-results="true" v-else />
        </template>
        <div v-else>
          <loader-screen/>
        </div>
      </div>
    </div>
    <div class="e-bottom-navbar">
      <div class="e-row e-row--gutters e-row--justify-between">
        <div class="e-col-auto e-order-sm-2" :class="{'e-col-sm-6': formattedProducts.length > 0, 'e-col-sm-12': formattedProducts.length < 1}">
          <button class="e-btn e-btn--prev" @click.prevent="previous">Previous</button>
        </div>
        <div class="e-col-auto e-col-sm-6 e-order-sm-3">
          <button class="e-btn e-btn--help" v-if="formattedProducts.length > 0" @click.prevent="nextQuestion(needHelp)">Need Help</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex';
import {
  needHelp,
  autoBuster,
  tankless,
  miniTank,
} from '@/utils/residential-flow';
import { selectCabinet } from '@/utils/commercial-flow';
import InfoText from '@/components/ui/InfoText.vue';
import ProductLink from '@/components/ui/ProductLink.vue';
import LoaderScreen from '@/components/ui/LoaderScreen.vue';
import NeedHelpBlock from '@/components/ui/NeedHelpBlock.vue';
import { baseUrl, FLOW_TYPE } from '@/store';

export default {
  components: {
    InfoText,
    ProductLink,
    LoaderScreen,
    NeedHelpBlock,
  },
  name: 'Result',
  data() {
    return {
      FLOW_TYPE,
      needHelp,
      activeTab: 0,
      whereToByLink: `${baseUrl}/find-a-rep`,
    };
  },
  mounted() {
    if (!this.withCabinet) {
      this.getProducts();
    }
  },
  computed: {
    ...mapState(['selectedFlow', 'products', 'containerHeight', 'isLoading', 'steps', 'selectedProduct', 'selectedCabinet', 'isMobile']),
    ...mapGetters(['currentQuestion', 'showQuantity', 'cabinetOptions']),
    isAutoBuster() {
      return this.currentQuestion.type === autoBuster;
    },
    isTankless() {
      return this.currentQuestion.type === tankless;
    },
    isMiniTank() {
      return this.currentQuestion.type === miniTank;
    },
    withCabinet() {
      return this.currentQuestion.withCabinet || false;
    },
    formattedProducts() {
      if (this.withCabinet) {
        return [this.selectedProduct];
      }
      return this.products.filter((product) => product.quantity > 0);
    },
  },
  methods: {
    ...mapActions(['previousQuestion', 'nextQuestion', 'getProducts', 'setSelectedProduct', 'setSelectedCabinetOptions']),
    previous() {
      this.previousQuestion();
    },
    setTab(index) {
      this.activeTab = index;
      if (this.isMobile) {
        this.$refs.resultContent.scrollIntoView({ behavior: 'smooth' });
      }
    },
    selectCabinet(product) {
      this.setSelectedProduct(product);
      this.setSelectedCabinetOptions([]);
      this.nextQuestion(selectCabinet);
    },
  },
};
</script>

<style lang="scss">

</style>
